import React from 'react';
import Breadcrumbs, { type BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import IconComponent from 'ui/src/components/Icon';
import extractText from 'utilities/src/extractText';
import twMerge from 'utilities/src/twMerge';
import PreviewPostError from 'app/components/PreviewPostError';

type Props = {
  title: string;
  excerpt?: string | null;
  breadcrumbsItems: BreadcrumbsItemType[];
  formattedDateModified?: string | null;
  dateTimeModified?: string | null;
  timeToRead?: number | null;
  preview: boolean;
  errors: string | null;
  className?: string;
};

export default function ArticleHero({
  title,
  excerpt,
  breadcrumbsItems,
  formattedDateModified,
  dateTimeModified,
  timeToRead,
  preview,
  errors,
  className,
}: Props) {
  return (
    <div className={twMerge('flex flex-col gap-8 pb-6 pt-12', className)}>
      {preview && errors && <PreviewPostError message={errors} />}
      <nav aria-label="Breadcrumb">
        <Breadcrumbs
          items={breadcrumbsItems}
          iconName="Home"
          iconClasses="bg-site-neutrals-900"
          customListClasses="text-site-neutrals-900 gap-y-1"
          customSeparatorClasses="px-1"
        />
      </nav>

      <div className="flex max-w-[75.625rem] flex-col gap-4">
        <h1 className="site-display-l md:site-display-xl text-site-neutrals-900">{title}</h1>
        {excerpt && (
          <p className="site-paragraph-l md:site-paragraph-xl text-site-neutrals-600">{extractText(excerpt)}</p>
        )}

        <div className="site-overline-s text-site-neutrals-600 flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
          {formattedDateModified && (
            <span className="flex items-center gap-2">
              <IconComponent iconName="CalendarMonth" size="IconS" className="bg-site-neutrals-600" />
              <span>
                Last updated: <time dateTime={dateTimeModified ?? undefined}>{formattedDateModified}</time>
              </span>
            </span>
          )}
          <span className="flex items-center gap-2">
            <IconComponent iconName="Schedule" size="IconS" className="bg-site-neutrals-600" />
            {timeToRead || 10} min read
          </span>
        </div>
      </div>
    </div>
  );
}
